import React, { useEffect, useRef, useState } from 'react';
import BreadCrumb from 'Common/BreadCrumb';
import Select from 'react-select';

// Icon
import { ShoppingBag, ChevronLeft, MoveRight, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { countryOptions } from 'Common/constants/countries';
import { BillingAddress } from 'Common/Models/Address/BillingAddress';
import { ShippingAddress } from 'Common/Models/Address/ShippingAddress';
import { WithMessagesProps } from 'Common/withMessages';
import withNotifications, { WithNotificationsProps } from 'Common/withNotifications';
import { WithTranslation } from 'react-i18next';
import { WithAuthProps } from 'Common/Components/Authentication/WithAuthProps';
import { WithCurrencyProps } from 'Common/Components/Currency/WithCurrencyProps';
import withMessages from 'Common/withMessages';
import withTranslation from 'Common/withTranslation';
import withAuth from 'Common/Components/Authentication/withAuth';
import { withCurrency } from 'Common/Components/Currency/withCurrency';
import BillingAddressEditor from 'pages/UserProfile/Addresses/BillingAddressEditor';
import { axios_helper_async } from 'helpers/Axios';
import { MessageType } from 'Common/Components/Messages/messageTypes';
import { NotificationType } from 'Common/Components/Notifications/notificationTypes';
import CheckboxRadio from 'pages/Components/Forms/CheckboxRadio';
import ShippingAddressEditor from 'pages/UserProfile/Addresses/ShippingAddressEditor';
import { CartProductView } from 'Common/Models/CartProductView';
import { DeliveryResponseViewModel } from 'Common/ViewModels/DeliveryResponseViewModel';
import { FedExRequestViewModel } from 'Common/ViewModels/FedEx/FedExRequestViewModel';
import languages from 'Common/constants/languages';
import Paypal from 'Common/Components/Paypal/Paypal';
import { countriesTranslations } from 'Common/constants/countriesLocales';
import { PaypalOrder } from 'Common/Models/Paypal/PaypalOrder';
import { PaypalRequestViewModel } from 'Common/ViewModels/Paypal/PaypalRequestViewModel';
import { Guid } from 'Common/Models/Guid';
import { CheckoutViewModel } from 'Common/ViewModels/Checkout/CheckoutViewModel';
import withRouter, { WithRouterProps } from 'Common/withRouter';
import { WithCartProps } from 'Common/Components/Cart/WithCartProps';
import { withCart } from 'Common/Components/Cart/withCart';
import { roundToTwoDecimals } from 'Common/constants/math';

const Checkout: React.FC<WithMessagesProps & WithNotificationsProps & WithTranslation & WithAuthProps & WithCurrencyProps & WithRouterProps & WithCartProps> = ({
    account,
    i18n,
    messages,
    notifications,
    t,
    c,
    currency,
    router,
    cart
}) => {
    const [billingAddress, setBillingAddress] = useState<BillingAddress>();
    const [shippingAddress, setShippingAddress] = useState<ShippingAddress>();
    const [products, setProducts] = useState<CartProductView[]>([]);
    const [subtotal, setSubtotal] = useState<number>(0);
    const [invoice, setInvoice] = useState<boolean>(false);
    const [shippingCharge, setShippingCharge] = useState<number>(0);
    const [total, setTotal] = useState<number>(0);
    const [taxAmount, setTaxAmount] = useState<number>(0);
    const [extraFee, setextraFee] = useState<number>(0);
    const fee = 0.04;

    const model = useRef<CheckoutViewModel>({
        customerId: Number(account.id),
        billingAddress: invoice ? billingAddress : undefined,
        invoice: invoice,
        paypalOrder: undefined,
        products: products,
        shippingAddress: shippingAddress,
        shippingCharge: shippingCharge,
        subtotal: subtotal,
        taxAmount: taxAmount,
        total: total,
        extraFee: extraFee
    });

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isOpenBillingAddressEditor, setIsOpenBillingAddressEditor] = useState<boolean>(false);
    const [isOpenShippingAddressEditor, setIsOpenShippingAddressEditor] = useState<boolean>(false);
    const [tax, setTax] = useState<number>(16);
    const [deliveryMethods, setDeliveryMethods] = useState<DeliveryResponseViewModel[]>([]);
    const [shipping, setShipping] = useState<string>();
    const [isDeliveryMethods, setIsDeliveryMethods] = useState<boolean>(false);    
    const [step, setStep] = useState<number>(0);
    const [isOpen, setIsOpen] = useState<boolean[]>([true, true, true]);
    const [force, setForce] = useState<boolean>(false);
    const [isOrderCreating, setIsOrderCreating] = useState<boolean>(false);

    const toggleAccordion = (index: number, value?: boolean) => {
        if (index < isOpen.length) {
            setIsOpen((prevModel) =>
                //prevModel.map((item, i) => (i === index ? true : false))
                prevModel.map((item, i) => (i === index ? (value !== undefined ? value : !item) : item))
            );
        }
    };

    const handleOnClose = async () => {

    }

    const getProducts = async () => {
        try {
            setIsLoading(true);
            const body = {
                customerId: account.id
            }

            const response = await axios_helper_async<CartProductView[]>("/Cart/Get-Cart-Products", 'GET', body);

            messages.setMessages(response.messages);
            notifications.setNotifications(response.notifications);

            if (response.isSuccess && response.data) {
                setProducts(response.data);
            }
        }
        catch (ex: any) {
            notifications.showNotification(`${ex}`, NotificationType.Error);
        }
        finally {
            setIsLoading(false);
        }
    }

    const handleGetCustomerBillingAddress = async () => {
        if (account.id) {
            try {
                setIsLoading(true);
                const body = {
                    id: account.id
                }

                const response = await axios_helper_async<BillingAddress>("/Account/Get-Customer-Billing-Address", 'GET', body);

                messages.setMessages(response.messages);
                notifications.setNotifications(response.notifications);

                if (response.isSuccess) {
                    if (response.data) {
                        setBillingAddress(response.data);
                    }
                }
                else {
                    messages.showMessage("UserProfile.Errors.GetCustomerBillingAddress", MessageType.Error, "Error");
                }
            }
            catch (err: any) {
                notifications.showNotification("AxiosAPI.FailedFetch", NotificationType.Error);
            }
            finally {
                setIsLoading(false);
            }
        }
    }

    const handleGetCustomerShippingAddress = async () => {
        if (account.id) {
            try {
                setIsLoading(true);
                const body = {
                    id: account.id
                }

                const response = await axios_helper_async<ShippingAddress>("/Account/Get-Customer-Shipping-Address", 'GET', body);

                messages.setMessages(response.messages);
                notifications.setNotifications(response.notifications);

                if (response.isSuccess) {
                    if (response.data) {
                        setShippingAddress(response.data);                        
                    }
                }
                else {
                    messages.showMessage("UserProfile.Errors.GetCustomerShippingAddress", MessageType.Error, "Error");
                }
            }
            catch (err: any) {
                notifications.showNotification("AxiosAPI.FailedFetch", NotificationType.Error);
            }
            finally {
                setIsLoading(false);
            }
        }
    }

    const handlePlaceOrder = async () => {
        
    }

    const handleDeliveryMethods = async () => {
        // if ((!isDeliveryMethods) && shippingAddress) {
        //     try {
        //         setForce(false);
        //         setIsDeliveryMethods(true);
        //         setIsLoading(true);
        //         const body: FedExRequestViewModel = {
        //             locale: `${i18n.language === languages.es.label ? `${i18n.language}-MX` : `${i18n.language}-US`}`,
        //             country: shippingAddress.country,
        //             zipCode: shippingAddress.zipCode,
        //             cartProductsView: products
        //         }

        //         const response = await axios_helper_async<DeliveryResponseViewModel[]>("/api/ShippingMethods/Get-Rates", 'POST', body);

        //         messages.setMessages(response.messages);
        //         notifications.setNotifications(response.notifications);

        //         if (response.isSuccess) {
        //             if (response.data && !isDeliveryMethods) {
        //                 const res = response.data;
        //                 setDeliveryMethods(res);
        //             }
        //         }
        //         else {
        //             messages.showMessage("UserProfile.Errors.GetCustomerShippingAddress", MessageType.Error, "Error");
        //             setIsDeliveryMethods(false);
        //         }

        //     }
        //     catch (err: any) {
        //         notifications.showNotification("AxiosAPI.FailedFetch", NotificationType.Error);
        //     }
        //     finally {
        //         setDeliveryMethods((prevModel) => [
        //             ...prevModel,
        //             {
        //               amount: 0,
        //               date: "-",
        //               id: Guid.empty,
        //               type: 'Checkout.DefaultDeliveryMethods.Type',
        //             },
        //         ]);
        //         setIsLoading(false);                
        //     }
        // }
    }

    const handleOnApprove = async (data: PaypalOrder, actions: any) => {
        if(data && data.orderID) {
            try {
                setIsOrderCreating(true);
                model.current.paypalOrder = data;
                
                // console.log(model.current);


                const response = await axios_helper_async<boolean>("/Cart/Create-Order", 'POST', model.current);

                messages.setMessages(response.messages);
                notifications.setNotifications(response.notifications);

                if (response.isSuccess && response.data && model.current.paypalOrder.orderID) {
                    notifications.showNotification("Checkout.Success.OrderCreated", NotificationType.Success);
                    await handleOnAuthorize(data);
                    router.navigate("/");
                    cart.updateCart();
                }
                else {
                    messages.showMessage("Checkout.Errors.NoOrderCreated", MessageType.Error, "Error");
                }
            }
            catch (err: any) {
                notifications.showNotification("AxiosAPI.FailedFetch", NotificationType.Error);
            }
            finally {
                setIsOrderCreating(false);
            }
        }
        else {
            messages.showMessage('Checkout.Errors.NoDataOrder', MessageType.Error, 'Checkout.Title');
        }
    }

    const handleOnAuthorize = async (order: PaypalOrder) => {
        try {
            setIsOrderCreating(true);

            // console.log(order);

            const response = await axios_helper_async<boolean>("/Cart/Authorize-Order", 'POST', order);

            messages.setMessages(response.messages);
            notifications.setNotifications(response.notifications);

            if (response.isSuccess && response.data) {
                notifications.showNotification("Checkout.Success.PaymentApproved", NotificationType.Success);                
            }
            else {
                messages.showMessage("Checkout.Errors.NoPaymentApproved", MessageType.Error, "Error");
            }
        }
        catch (err: any) {
            notifications.showNotification("AxiosAPI.FailedFetch", NotificationType.Error);
        }
        finally {
            setIsOrderCreating(false);
        }
    }

    
    const handleOnCancel = async (data: PaypalOrder, actions: any) => {
        // console.log(data);
        
    }

    const getData = async () => {
        await handleGetCustomerBillingAddress();
        await handleGetCustomerShippingAddress();
    }

    const handleCloseBillingAddress = () => {
        setIsOpenBillingAddressEditor(false);
    }

    const handleCloseShippingAddress = () => {
        setIsOpenShippingAddressEditor(false);
        setForce(true);
    }

    const handleInvoiceOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { checked } = e.target

        setInvoice(checked);
    }

    useEffect(() => {
        setSubtotal(roundToTwoDecimals(products.reduce((sum, prodcut) => sum += (prodcut.price * prodcut.quantity), 0)));
    }, [products, shippingCharge])

    useEffect(() => {
        setShippingCharge(roundToTwoDecimals(deliveryMethods.find(delivery => delivery.id === shipping)?.amount ?? 0));        
    }, [shipping])

    useEffect(() => {
        setTaxAmount(roundToTwoDecimals(subtotal * (tax / 100)))
    }, [subtotal, shippingCharge])
    
    useEffect(() => {
        const _total = roundToTwoDecimals((subtotal) * ((tax / 100) + 1) + shippingCharge)
        // console.log(_total);
        const _extraFee = roundToTwoDecimals(_total * fee);
        // console.log(_extraFee);
        setTotal(roundToTwoDecimals(_total + _extraFee))        
        setextraFee(_extraFee);
    }, [taxAmount, shippingCharge, subtotal])

    useEffect(() => {
        (async () => {
            await getData();
        })();
        (async () => {
            await getProducts();
        })()
    }, [account.id])

    useEffect(() => {
        if (account.id && !isOpenBillingAddressEditor) {
            (async () => {
                await handleGetCustomerBillingAddress();
            })();
        }
    }, [isOpenBillingAddressEditor]);

    useEffect(() => {

        if (account.id && !isOpenShippingAddressEditor && !shippingAddress) {
            (async () => {
                await handleGetCustomerShippingAddress();
            })();
        }
        else if (isOpenShippingAddressEditor) {
            setShippingAddress(undefined);
        }
    }, [isOpenShippingAddressEditor, shippingAddress]);

    useEffect(() => {
        if (products && products.length > 0 && shippingAddress && shippingAddress.zipCode && shippingAddress.country && (!deliveryMethods || deliveryMethods.length === 0 || force)) {
            (async () => {
                await handleDeliveryMethods()
            })();
        }
    }, [products, shippingAddress, isDeliveryMethods])

    useEffect(() => {
        if(force){
            setDeliveryMethods([]);
            setForce(false);
        }
    }, [force])

    useEffect(() => {
        if(deliveryMethods.length === 0) {
            setIsDeliveryMethods(false);
        }
        if(!shipping && deliveryMethods.length > 0) {
            setShipping(deliveryMethods[0].id)
        }
    }, [deliveryMethods])

    useEffect(() => {
        toggleAccordion(step);
    }, [step]);

    useEffect(() => {
        model.current = {
          billingAddress: invoice ? billingAddress : undefined,
          invoice: invoice,
          paypalOrder: undefined,
          products: products,
          shippingAddress: shippingAddress,
          shippingCharge: shippingCharge,
          subtotal: subtotal,
          taxAmount: taxAmount,
          total: total,
          extraFee: extraFee,
          customerId: Number(account.id)
        };
      }, [billingAddress, invoice, products, shippingAddress, shippingCharge, subtotal, taxAmount, total, extraFee, account.id]);

    return (
        <React.Fragment>
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-x-5">
                <div className="xl:col-span-8">
                    <div className="flex items-center gap-3 mb-5">
                        <div className="grow">
                            <Link to="/cart" className="transition-all duration-300 ease-linear text-custom-500 hover:text-custom-600"><ChevronLeft className="inline-block w-4 h-4 align-middle ltr:mr-1 rtl:ml-1 rtl:rotate-180" /> <span className="align-middle">{t('Checkout.BackToCart')}</span></Link>
                        </div>
                    </div>
                    <div className="card border rounded-xl mb-4">
                        <div
                            className="card-header flex justify-between items-center cursor-pointer p-4 bg-gray-100"
                            onClick={() => setStep(0)}
                        >
                            <h6 className="text-15">{t('Checkout.ShippingInformation')}</h6>
                            <button className="text-custom-500">
                                {isOpen[0] ? <ChevronUp /> : <ChevronDown />}
                            </button>
                        </div>
                        {isOpen[0] && (
                            <div className="flex flex-col card-body gap-3">
                                <div className="flex flex-row justify-end items-center w-full">
                                    {/* <h6 className="mb-4 text-15">Shipping Information</h6> */}
                                    <div className="flex items-center gap-2">
                                        <input
                                            id="checkboxInvoice"
                                            className="w-4 h-4 border rounded-sm appearance-none cursor-pointer bg-slate-100 border-slate-200 dark:bg-zink-600 dark:border-zink-500 checked:bg-custom-500 checked:border-custom-500 dark:checked:bg-custom-500 dark:checked:border-custom-500 checked:disabled:bg-custom-400 checked:disabled:border-custom-400"
                                            type="checkbox"
                                            checked={invoice}
                                            onChange={handleInvoiceOnChange}
                                            defaultChecked
                                        />
                                        <label htmlFor="checkboxInvoice" className="align-middle cursor-pointer">
                                            {t('Checkout.Invoice')}
                                        </label>
                                    </div>
                                </div>
                                <div className="flex flex-col md:flex-row gap-5 md:gap-2 w-full justify-start items-start">
                                    {invoice && <div className="flex flex-col gap-2 justify-center items-start bg-slate-50 border border-gray-300 rounded-xl p-4 w-full">
                                        {!isLoading ? (
                                            <>
                                                <div className="flex flex-row justify-between items-center w-full">
                                                    <h3 className="text-custom-500">{t("UserProfile.BillingAddressEditor.Title")}</h3>
                                                    <span className="underline text-custom-500 cursor-pointer" onClick={() => setIsOpenBillingAddressEditor(true)}>{t('General.Edit')}</span>
                                                </div>
                                                <table className="text-left">
                                                    <tbody>
                                                        <tr>
                                                            <td className="font-bold pr-4">{t("UserProfile.BillingAddressEditor.TaxId")}:</td>
                                                            <td className="py-0">{billingAddress?.taxId ?? "-"}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-bold pr-4">{t("UserProfile.BillingAddressEditor.Street")}:</td>
                                                            <td className="py-0">{billingAddress?.street ?? "-"}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-bold pr-4">{t("UserProfile.BillingAddressEditor.AddressLine")}:</td>
                                                            <td className="py-0">{billingAddress?.addressLine ?? "-"}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-bold pr-4">{t("UserProfile.BillingAddressEditor.City")}:</td>
                                                            <td className="py-0">{billingAddress?.city ?? "-"}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-bold pr-4">{t("UserProfile.BillingAddressEditor.State")}:</td>
                                                            <td className="py-0">{billingAddress?.state ?? "-"}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-bold pr-4">{t("UserProfile.BillingAddressEditor.ZipCode")}:</td>
                                                            <td className="py-0">{billingAddress?.zipCode ?? "-"}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-bold pr-4">{t("UserProfile.BillingAddressEditor.Country")}:</td>
                                                            <td className="py-0">{countriesTranslations.find(traslations => traslations.language === i18n.language)?.values.find(countries => countries.value === billingAddress?.country)?.label ?? "-"}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-bold pr-4">{t("UserProfile.BillingAddressEditor.Company")}:</td>
                                                            <td className="py-0">{billingAddress?.company ?? "-"}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-bold pr-4">{t("UserProfile.BillingAddressEditor.Phone")}:</td>
                                                            <td className="py-0">{billingAddress?.phone ?? "-"}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                                <BillingAddressEditor address={billingAddress} isOpen={isOpenBillingAddressEditor} onClose={handleOnClose} close={handleCloseBillingAddress}></BillingAddressEditor>
                                            </>
                                        ) : (
                                            // Animación de carga si está cargando
                                            <>
                                                <h3 className="text-custom-500">{t("UserProfile.BillingAddressEditor.Title")}</h3>
                                                <table className="text-left">
                                                    <tbody>
                                                        {['TaxId', 'Street', 'AddressLine', 'City', 'State', 'ZipCode', 'Country', 'Company', 'Phone'].map((label, index) => (
                                                            <tr key={index}>
                                                                <td className="font-bold pr-4">{t(`UserProfile.BillingAddressEditor.${label}`)}:</td>
                                                                <td className="py-1 w-56">
                                                                    <div className="w-full h-6 bg-gray-300 animate-pulse rounded"></div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </>
                                        )}
                                    </div>}
                                    <div className="flex flex-col gap-2 justify-center items-start bg-slate-50 border border-gray-300 rounded-xl p-4 w-full">
                                        {!isLoading ? (
                                            <>
                                                <div className="flex flex-row justify-between items-center w-full">
                                                    <h3 className="text-custom-500">{t("UserProfile.ShippingAddressEditor.Title")}</h3>
                                                    <span className="underline text-custom-500 cursor-pointer" onClick={() => setIsOpenShippingAddressEditor(true)}>{t('General.Edit')}</span>
                                                </div>
                                                <table className="text-left">
                                                    <tbody>
                                                        <tr>
                                                            <td className="font-bold pr-4">{t("UserProfile.ShippingAddressEditor.Street")}:</td>
                                                            <td className="py-0">{shippingAddress?.street ?? "-"}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-bold pr-4">{t("UserProfile.ShippingAddressEditor.AddressLine")}:</td>
                                                            <td className="py-0">{shippingAddress?.addressLine ?? "-"}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-bold pr-4">{t("UserProfile.ShippingAddressEditor.City")}:</td>
                                                            <td className="py-0">{shippingAddress?.city ?? "-"}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-bold pr-4">{t("UserProfile.ShippingAddressEditor.State")}:</td>
                                                            <td className="py-0">{shippingAddress?.state ?? "-"}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-bold pr-4">{t("UserProfile.ShippingAddressEditor.ZipCode")}:</td>
                                                            <td className="py-0">{shippingAddress?.zipCode ?? "-"}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-bold pr-4">{t("UserProfile.ShippingAddressEditor.Country")}:</td>
                                                            <td className="py-0">{countriesTranslations.find(traslations => traslations.language === i18n.language)?.values.find(countries => countries.value === shippingAddress?.country)?.label ?? "-"}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-bold pr-4">{t("UserProfile.ShippingAddressEditor.Company")}:</td>
                                                            <td className="py-0">{shippingAddress?.company ?? "-"}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-bold pr-4">{t("UserProfile.ShippingAddressEditor.Phone")}:</td>
                                                            <td className="py-0">{shippingAddress?.phone ?? "-"}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                                <ShippingAddressEditor address={shippingAddress} isOpen={isOpenShippingAddressEditor} onClose={handleOnClose} close={handleCloseShippingAddress}></ShippingAddressEditor>
                                            </>
                                        ) : (
                                            // Animación de carga si está cargando
                                            <>
                                                <h3 className="text-custom-500">{t("UserProfile.ShippingAddressEditor.Title")}</h3>
                                                <table className="text-left">
                                                    <tbody>
                                                        {['Street', 'AddressLine', 'City', 'State', 'ZipCode', 'Country', 'Company', 'Phone'].map((label, index) => (
                                                            <tr key={index}>
                                                                <td className="font-bold pr-4">{t(`UserProfile.ShippingAddressEditor.${label}`)}:</td>
                                                                <td className="py-1 w-56">
                                                                    <div className="w-full h-6 bg-gray-300 animate-pulse rounded"></div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    {/* <div className="card border rounded-xl mb-4">
                        <div
                            className="card-header flex justify-between items-center cursor-pointer p-4 bg-gray-100"
                            onClick={() => setStep(1)}
                        >
                            <h6 className="text-15">{t('Checkout.Delivery')}</h6>
                            <button className="text-custom-500">
                                {isOpen[1] ? <ChevronUp /> : <ChevronDown />}
                            </button>
                        </div>
                        {isOpen[1] && (
                            <div className="card-body">
                                <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
                                    {deliveryMethods.map((method, key) => {
                                        return (
                                            <div className="flex items-center gap-3" key={method.id} onClick={() => setShipping(method.id)}>
                                                <input id={method.id} className="w-4 h-4 border rounded-full appearance-none cursor-pointer bg-slate-100 border-slate-200 dark:bg-zink-600 dark:border-zink-500 checked:bg-purple-500 checked:border-purple-500 dark:checked:bg-purple-500 dark:checked:border-purple-500 checked:disabled:bg-purple-400 checked:disabled:border-purple-400 peer" type="radio" name={method.id} checked={method.id === shipping}/>
                                                <label htmlFor={method.id} className="flex flex-col gap-4 p-5 border rounded-md cursor-pointer md:flex-row border-slate-200 dark:border-zink-500 peer-checked:border-purple-500 dark:peer-checked:border-purple-700 grow">
                                                    <span className="grow">
                                                        <span className="block mb-1 font-semibold text-15">{t(method.type)}</span>
                                                        <span className="text-slate-500 dark:text-zink-200">{t('Checkout.ExpectedDelivery')}: {isNaN(new Date(method.date).getTime()) ? method.date : new Date(method.date).toLocaleDateString(`${i18n.language === languages.es.label ? `${i18n.language}-MX` : `${i18n.language}-US`}`)}</span>
                                                    </span>
                                                    <span className="shrink-0">
                                                        <span className="block text-lg font-semibold">${c(method.amount).toFixed(2)} ({currency.value})</span>
                                                    </span>
                                                </label>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )}
                    </div> */}
                    {/* <div className="card border rounded-xl mb-4">
                        <div
                            className="card-header flex justify-between items-center cursor-pointer p-4 bg-gray-100"
                            onClick={() => setStep(2)}
                        >
                            <h6 className="text-15">{t('Checkout.Payment')}</h6>
                            <button className="text-custom-500">
                                {isOpen[2] ? <ChevronUp /> : <ChevronDown />}
                            </button>
                        </div>
                        {isOpen[2] && (
                            <div className="card-body">
                                {shipping && shippingAddress && <Paypal disabled={isOrderCreating} onApprove={handleOnApprove} onCancel={handleOnCancel} products={products} subtotalAmount={subtotal} taxAmount={taxAmount} shippingAmount={shippingCharge} extraFee={extraFee}/>}
                            </div>
                        )}
                    </div> */}
                </div>
                <div className="xl:col-span-4">
                    <div className="card">
                        <div className="card-body">
                            <h6 className="mb-4 text-15">{t('Checkout.OrderSummary')}</h6>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <tbody>
                                        {
                                            products.map((product, key) => {
                                                return (
                                                    <tr key={key}>
                                                        <td className="px-3.5 py-4 border-b border-dashed first:pl-0 last:pr-0 border-slate-200 dark:border-zink-500">
                                                            <div className="flex items-center gap-3">
                                                                <div className="flex items-center justify-center w-12 h-12 rounded-md bg-slate-100 shrink-0">
                                                                    <img src={product.image} alt="" className="h-8" />
                                                                </div>
                                                                <div className="grow">
                                                                    <h6 className="mb-1 text-15">{product.name}</h6>
                                                                    <p className="text-slate-500 dark:text-zink-200">${c(product.price).toFixed(2)} x {product.quantity}</p>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-3.5 py-4 border-b border-dashed first:pl-0 last:pr-0 border-slate-200 dark:border-zink-500 ltr:text-right rtl:text-left">${c(product.price * product.quantity).toFixed(2)}</td>
                                                    </tr>
                                                )
                                            })
                                        }
                                        <tr>
                                            <td className="px-3.5 pt-4 pb-3 first:pl-0 last:pr-0 text-slate-500 dark:text-zink-200">
                                                {t('Checkout.Subtotal')}
                                            </td>
                                            <td className="px-3.5 pt-4 pb-3 first:pl-0 last:pr-0 ltr:text-right rtl:text-left">${c(subtotal).toFixed(2)}</td>
                                        </tr>
                                        <tr>
                                            <td className="px-3.5 py-3 first:pl-0 last:pr-0 text-slate-500 dark:text-zink-200">
                                                {t('Checkout.EstimatedTax')} ({c(tax).toFixed(0)}%)
                                            </td>
                                            <td className="px-3.5 py-3 first:pl-0 last:pr-0 ltr:text-right rtl:text-left">${c(taxAmount).toFixed(2)}</td>
                                        </tr>
                                        <tr>
                                            <td className="px-3.5 py-3 first:pl-0 last:pr-0 text-slate-500 dark:text-zink-200">
                                                {t('Checkout.ShippingCharge')}
                                            </td>
                                            <td className="px-3.5 py-3 first:pl-0 last:pr-0 ltr:text-right rtl:text-left">${c(shippingCharge).toFixed(2)}</td>
                                        </tr>
                                        <tr>
                                            <td className="px-3.5 py-3 first:pl-0 last:pr-0 text-slate-500 dark:text-zink-200">
                                                {t('Checkout.extraFee')} (4%)
                                            </td>
                                            <td className="px-3.5 py-3 first:pl-0 last:pr-0 ltr:text-right rtl:text-left">${c(extraFee).toFixed(2)}</td>
                                        </tr>
                                        <tr className="font-semibold">
                                            <td className="px-3.5 pt-3 first:pl-0 last:pr-0 text-slate-500 dark:text-zink-200">
                                                {t('Checkout.Total')} ({currency.value})
                                            </td>
                                            <td className="px-3.5 pt-3 first:pl-0 last:pr-0 ltr:text-right rtl:text-left">${c(total).toFixed(2)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div className="mt-4">
                                <button type="button" disabled={!(!shipping && (!products || products.length === 0))} onClick={handlePlaceOrder} className="w-full text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"><span className="align-middle">Place Order</span> <MoveRight className="inline-block w-4 h-4 align-middle ltr:ml-1 rtl:mr-1 rtl:rotate-180" /></button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default withMessages(withNotifications(withTranslation(withAuth(withCurrency(withRouter(withCart(Checkout)))))));
//WithMessagesProps & WithNotificationsProps & WithTranslation & WithAuthProps & WithCurrencyProps